/**
 * Production helpers for canned responses with optional attachments.
 * Used by settings forms, ReplyBox composer materialization, and WhatsApp multi-message payload.
 */
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';

/**
 * True when content has visible text after stripping HTML (e.g. "<p></p>" is empty).
 * @param {string} content
 * @returns {boolean}
 */
export function hasMeaningfulCannedContent(content) {
  const text = String(content || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > 0;
}

/**
 * Settings form rule: text and/or files required.
 * @param {string} content
 * @param {File[]} [newFiles]
 * @param {Array} [retainedFiles] existing files kept on edit
 * @returns {boolean}
 */
export function hasCannedContentOrFiles(
  content,
  newFiles = [],
  retainedFiles = []
) {
  return (
    hasMeaningfulCannedContent(content) ||
    (newFiles && newFiles.length > 0) ||
    (retainedFiles && retainedFiles.length > 0)
  );
}

/**
 * Normalize API canned file payload for composer consumers.
 * API shape: { id, filename, content_type, byte_size, url }
 * @param {Array} files
 * @returns {Array}
 */
export function normalizeCannedFilesFromApi(files = []) {
  if (!files || !files.length) return [];
  return files.map(file => ({
    id: file.id,
    filename: file.filename,
    content_type: file.content_type,
    byte_size: file.byte_size,
    url: file.url,
  }));
}

/**
 * Materialize a canned API file + fetched blob into ReplyBox onFileUpload shape.
 * @param {{ filename: string, content_type?: string, byte_size?: number }} file
 * @param {Blob} blob
 * @returns {{ name: string, type: string, size: number, file: File }}
 */
export function materializeCannedFileForUpload(file, blob) {
  const type = file.content_type || blob.type || '';
  const fileObj = new File([blob], file.filename, { type });
  return {
    name: file.filename,
    type,
    size: file.byte_size ?? fileObj.size,
    file: fileObj,
  };
}

/**
 * Fetch canned-response file URLs and materialize only successful ones for the composer.
 * Failures are collected; callers must surface visible feedback and must not pretend failed files attached.
 *
 * @param {Array<{ url: string, filename: string, content_type?: string, byte_size?: number }>} files
 * @param {{ fetchImpl?: typeof fetch }} [options]
 * @returns {Promise<{ attached: Array, failed: Array }>}
 */
export async function materializeCannedFilesForComposer(
  files,
  { fetchImpl = fetch } = {}
) {
  const attached = [];
  const failed = [];
  if (!files || !files.length) return { attached, failed };

  // Sequential to keep upload order stable in ReplyBox.
  // eslint-disable-next-line no-restricted-syntax
  for (const file of files) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetchImpl(file.url);
      if (!response.ok) {
        throw new Error(`Failed to fetch canned file: ${response.status}`);
      }
      // eslint-disable-next-line no-await-in-loop
      const blob = await response.blob();
      attached.push(materializeCannedFileForUpload(file, blob));
    } catch (error) {
      failed.push({ file, error });
    }
  }

  return { attached, failed };
}

/**
 * Filter materialized canned uploads with the same channel gate used by paste/upload.
 * Rejected files must not enter the composer; callers surface a translated alert.
 *
 * Uses the real `isFileTypeAllowedForChannel` (no shadow implementation).
 *
 * @param {Array<{ name?: string, type?: string, size?: number, file?: File }>} uploads
 * @param {object} channelOptions options passed through to isFileTypeAllowedForChannel
 * @returns {{ accepted: Array, rejected: Array }}
 */
export function filterCannedUploadsForChannel(
  uploads = [],
  channelOptions = {}
) {
  const accepted = [];
  const rejected = [];

  (uploads || []).forEach(upload => {
    // Prefer the File instance (same shape as paste path); fall back to upload meta.
    const file = upload?.file || upload;
    if (isFileTypeAllowedForChannel(file, channelOptions)) {
      accepted.push(upload);
    } else {
      rejected.push(upload);
    }
  });

  return { accepted, rejected };
}

/**
 * WhatsApp multi-message payload core used by ReplyBox for non-IG/TikTok channels.
 * Attachment-only keeps message empty on every item; text+files captions first only.
 *
 * @param {object} options
 * @param {string} [options.message]
 * @param {Array} [options.attachedFiles]
 * @param {boolean} [options.directUploadsEnabled]
 * @returns {Array<{ message: string, files?: Array }>}
 */
export function buildWhatsAppMultipleMessagesPayload({
  message = '',
  attachedFiles = [],
  directUploadsEnabled = false,
}) {
  const multipleMessagePayload = [];

  if (attachedFiles && attachedFiles.length) {
    let caption = message;
    attachedFiles.forEach(attachment => {
      const attachedFile = directUploadsEnabled
        ? attachment.blobSignedId
        : attachment.resource.file;
      multipleMessagePayload.push({
        files: [attachedFile],
        message: caption,
      });
      // Only the first attachment receives the caption.
      caption = '';
    });
    return multipleMessagePayload;
  }

  multipleMessagePayload.push({ message });
  return multipleMessagePayload;
}
